import { cn } from '../../utils'
import { getPlanDisplayName } from '../../utils/billingUtils'
import { legacyPlanSunsetCopy } from '../../utils/legacy-plan-sunset'
import { useEnrichedBilling } from '../../hooks/useEnrichedBilling'
import { useEffectiveBillingPlan } from '../../hooks/use-effective-billing-plan'
import { useLegacyPlanSunset } from '../../hooks/use-legacy-plan-sunset'
import { useCreditsTopUp } from '../../hooks/use-credits-top-up'
import { useMemo, useCallback } from 'react'
import { Button } from '@highlight/ui'

export interface PlanDetailsBlockProps {
  /**
   * Callback when the manage subscription button is clicked
   */
  onManageSubscription: () => void

  /**
   * Optional className for styling
   */
  className?: string
}

/**
 * PlanDetailsBlock component displays the current plan with renewal/remaining days info
 */
export const PlanDetailsBlock: React.FC<PlanDetailsBlockProps> = ({ onManageSubscription, className }) => {
  const {
    subscription,
    isFree,
    isTrial,
    isPro,
    daysRemaining,
    renewalDateText,
    cancellationEndDateText,
    paywallState,
  } = useEnrichedBilling()
  const { plan: effectivePlan } = useEffectiveBillingPlan()
  const sunset = useLegacyPlanSunset()
  const { tier: creditsTier, openCreditsPurchase } = useCreditsTopUp()

  // Pro users below 80% usage, on a plan a top-up can actually bill. A legacy
  // plan resolves no tier, so the button would open nothing.
  const showAddCreditsButton = isPro && creditsTier !== null && !paywallState.atWarning && !paywallState.atLimit

  const handleAddCreditsClick = useCallback(() => {
    window.ampltd.trackEvent('Add credits', {
      action: 'initiated',
      from: 'plan details block',
      plan: paywallState.plan,
    })
    openCreditsPurchase('plan details block')
  }, [paywallState.plan, openCreditsPurchase])

  // Effective plan: pro/pro-trial present as the v2 trial, except a plan being
  // retired, which keeps its own name so this row agrees with the notice above it.
  const retiringPlanName = sunset ? legacyPlanSunsetCopy(sunset).planName : undefined
  const planName = useMemo(() => {
    if (isFree) return 'Free Plan'
    if (retiringPlanName) return retiringPlanName
    return getPlanDisplayName(effectivePlan)
  }, [isFree, retiringPlanName, effectivePlan])

  // Get time info text (either days remaining or renewal date)
  // Don't show time info for free users
  const timeInfoText = useMemo(() => {
    if (isFree) return null

    // If cancelled, show cancellation end date
    if (subscription.cancelled && cancellationEndDateText) {
      return cancellationEndDateText
    }

    // If trial, show days remaining
    if (isTrial && daysRemaining !== null) {
      if (daysRemaining < 0) {
        return 'Trial ended'
      }
      return `${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} remaining`
    }

    // If pro with renewal date, show renewal info
    if (isPro && renewalDateText) {
      return renewalDateText
    }

    return null
  }, [isFree, subscription.cancelled, cancellationEndDateText, isTrial, daysRemaining, isPro, renewalDateText])

  return (
    <div
      className={cn(
        'bg-foreground/[2%] flex flex-col gap-3 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between',
        className,
      )}
    >
      {/* Left side: Plan name, Current badge, and time info */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="bg-green h-1.5 w-1.5 rounded-full" />
        <h3 className="text-foreground-secondary m-0 text-[15px] font-medium">{planName}</h3>
        <div className="bg-element-hover flex items-center justify-center gap-[10px] rounded-lg px-2 py-[3px]">
          <span className="text-foreground-secondary text-[13px] font-medium leading-[20px]">Current</span>
        </div>
        {timeInfoText && (
          <div className="flex items-start justify-start gap-[10px] pl-2">
            <span className="text-foreground-placeholder text-sm">{timeInfoText}</span>
          </div>
        )}
      </div>

      {/* Right side: Add Credits and Manage subscription buttons */}
      {(isPro || isTrial) && (
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
          {showAddCreditsButton && (
            <Button variant="primary" onClick={handleAddCreditsClick}>
              Add Credits
            </Button>
          )}
          <Button variant="outline" onClick={onManageSubscription}>
            Manage Subscription
          </Button>
        </div>
      )}
    </div>
  )
}
